import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { Modal } from '@/components';
import { ButtonSmall } from '@/components/Button';
import { FormGroup, Input, LogoPicker, Radio } from '@/components/Inputs';
import { UnitInput } from '@/components/UnitInput/UnitInput';
import { useUnitPreferences } from '@/hooks/useUnitPreferences';
import type { CreateVesselPayload } from '@/store/vessels/vessel.dto';
import { useVessels } from '@/store/vessels/vessels.store';
import { toast } from '@/utils/toast.ts';
import { toCanonicalFt } from '@/utils/unitConversion';

import sharedStyles from '../CreateSheet.module.scss';

import {
  CREATED_AS_OPTIONS,
  DEFAULT_CREATED_AS,
  getStoredCreatedAs,
  storeCreatedAs,
  type VesselCreatedAs,
} from './createdAsPreference';
import styles from './CreateVesselSheet.module.scss';
import EmailConflictModal from './EmailConflictModal';
import { generateEmailFromVesselName } from './emailUtils';
import {
  VESSEL_DEFAULT_FORM_VALUES,
  vesselFormSchema,
  type VesselFormValues,
} from './vesselFormSchema';

type CreateVesselSheetProps = {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void | Promise<void>;
  title: string;
  closable?: boolean;
};

const CreateVesselSheet = ({
  open,
  onClose,
  onSuccess,
  title,
  closable = true,
}: CreateVesselSheetProps) => {
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [suggestedEmail, setSuggestedEmail] = useState('');
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [createdAs, setCreatedAs] = useState<VesselCreatedAs>(
    () => getStoredCreatedAs() ?? DEFAULT_CREATED_AS,
  );
  const { createVessel, checkVesselEmailExists } = useVessels();
  const { lengthUnit } = useUnitPreferences();

  // Re-seed from the stored preference on every open: callers keep this sheet mounted, so another
  // instance may have written a newer choice since this one last rendered.
  const wasOpen = useRef(open);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setCreatedAs(getStoredCreatedAs() ?? DEFAULT_CREATED_AS);
    }
    wasOpen.current = open;
  }, [open]);

  const defaultValues = useMemo<VesselFormValues>(() => VESSEL_DEFAULT_FORM_VALUES, []);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting, isValid },
    reset,
    getValues,
  } = useForm<VesselFormValues>({
    resolver: zodResolver(vesselFormSchema),
    defaultValues,
    mode: 'onChange',
  });

  const proceedWithCreation = async (values: VesselFormValues, internalEmail: string) => {
    const payload: CreateVesselPayload = {
      vessel_name: values.vesselName,
      make: values.vesselMake,
      model: values.vesselModel,
      year_built: values.yearBuilt ? parseInt(values.yearBuilt, 10) : undefined,
      length_overall: values.lengthOverall
        ? Math.round(toCanonicalFt(parseFloat(values.lengthOverall), lengthUnit))
        : undefined,
      country_registration: values.countryRegistration,
      registration_number: values.registrationNumber,
      structure_number_type: values.hullId ? 'hull_id' : undefined,
      structure_number_value: values.hullId,
      description: values.description?.trim() || '',
      image: photoFile,
      crew: [],
      internal_email: internalEmail,
      created_as: createdAs,
      timezone: values.timezone,
    };

    await createVessel(payload);
    storeCreatedAs(createdAs);

    await onSuccess?.();
    onClose();
    reset(defaultValues);
    setPhotoFile(null);
  };

  const onSubmit = async (values: VesselFormValues) => {
    try {
      const email = generateEmailFromVesselName(values.vesselName);
      const exists = await checkVesselEmailExists(email);

      if (exists) {
        setSuggestedEmail(email);
        setEmailModalOpen(true);
        return;
      }

      await proceedWithCreation(values, email).then(() => {
        toast.success('Vessel created');
      });
    } catch (error) {
      toast.error('Couldn’t create the vessel. Please try again.');
      console.error('Error creating vessel:', error);
    }
  };

  const handleEmailContinue = async (modifiedEmail: string) => {
    setIsCheckingEmail(true);
    try {
      const exists = await checkVesselEmailExists(modifiedEmail);

      if (exists) {
        setSuggestedEmail(modifiedEmail);
        return;
      }

      setEmailModalOpen(false);
      await proceedWithCreation(getValues(), modifiedEmail);
    } catch (error) {
      console.error('Error checking email:', error);
    } finally {
      setIsCheckingEmail(false);
    }
  };

  const handleClose = () => {
    onClose();
    reset(defaultValues);
    setPhotoFile(null);
  };

  return (
    <Modal open={open} onClose={handleClose} title={title} closable={closable}>
      <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
        <div className={styles.scrollContainer}>
          <div className={styles.photoUpload}>
            <LogoPicker round={false} compact onChange={(file) => setPhotoFile(file)} />
          </div>

          <FormGroup label="My role on this vessel" cols={CREATED_AS_OPTIONS.length}>
            {CREATED_AS_OPTIONS.map((option) => (
              <Radio
                key={option.value}
                label={option.label}
                checked={createdAs === option.value}
                onChange={() => setCreatedAs(option.value)}
              />
            ))}
          </FormGroup>

          <Input
            mandatory
            label="Vessel Name"
            placeholder="Vessel Name"
            error={errors.vesselName?.message}
            {...register('vesselName')}
          />
          <Input
            label="Vessel Make"
            placeholder="Benetti"
            error={errors.vesselMake?.message}
            {...register('vesselMake')}
          />
          <Input
            label="Vessel Model"
            placeholder="Vessel Model"
            error={errors.vesselModel?.message}
            {...register('vesselModel')}
          />
          <Controller
            name="lengthOverall"
            control={control}
            render={({ field }) => (
              <UnitInput
                kind="length"
                label="Length Overall"
                placeholder="Length Overall"
                type="number"
                error={errors.lengthOverall?.message}
                value={field.value ?? ''}
                onChange={field.onChange}
              />
            )}
          />
        </div>

        <div className={styles.footer}>
          <ButtonSmall
            className={sharedStyles.submit}
            type="submit"
            disabled={!isValid || isSubmitting}
          >
            Save
          </ButtonSmall>
        </div>
      </form>
      <EmailConflictModal
        open={emailModalOpen}
        email={suggestedEmail}
        onClose={() => setEmailModalOpen(false)}
        onContinue={handleEmailContinue}
        isChecking={isCheckingEmail}
      />
    </Modal>
  );
};

export default CreateVesselSheet;
